import { getDashboardWidget } from './widgetRegistry';
import {
  DashboardColumnCount,
  DashboardLayout,
  DashboardWidgetId,
  DashboardWidgetPlacement,
  DashboardWidthSpan,
  DEFAULT_DASHBOARD_LAYOUT,
} from './types';
import { buildDashboardLayoutRows } from './layoutRows';

export const MIN_VISIBLE_DASHBOARD_WIDGETS = 1;
export const MAX_DASHBOARD_COLUMN_COUNT = 2;

export interface DashboardWidgetMoveOptions {
  readonly forceSingleColumn?: boolean;
}

export type DashboardWidgetMoveDirection = 'up' | 'down' | 'left' | 'right';

export const cloneDashboardLayout = (layout: DashboardLayout): DashboardLayout => ({
  columnCount: layout.columnCount,
  widgets: layout.widgets.map((widget) => ({
    id: widget.id,
    order: widget.order,
    widthSpan: widget.widthSpan,
  })),
});

export const getDefaultDashboardLayout = (): DashboardLayout =>
  cloneDashboardLayout(DEFAULT_DASHBOARD_LAYOUT);

const toComparableWidgets = (
  widgets: readonly DashboardWidgetPlacement[]
): Array<{ id: DashboardWidgetId; order: number; widthSpan: DashboardWidthSpan }> =>
  [...widgets]
    .map((widget) => ({
      id: widget.id,
      order: widget.order,
      widthSpan: widget.widthSpan,
    }))
    .sort((left, right) => left.order - right.order || left.id.localeCompare(right.id));

export const areDashboardLayoutsEqual = (
  left: DashboardLayout,
  right: DashboardLayout
): boolean =>
  JSON.stringify({
    columnCount: left.columnCount,
    widgets: toComparableWidgets(left.widgets),
  }) ===
  JSON.stringify({
    columnCount: right.columnCount,
    widgets: toComparableWidgets(right.widgets),
  });

const withSequentialOrder = (
  widgets: readonly DashboardWidgetPlacement[]
): DashboardWidgetPlacement[] =>
  [...widgets]
    .sort((left, right) => left.order - right.order)
    .map((widget, index) => ({
      ...widget,
      order: index,
    }));

export const isWidgetVisibleInLayout = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId
): boolean => layout.widgets.some((widget) => widget.id === widgetId);

export const canRemoveDashboardWidget = (layout: DashboardLayout): boolean =>
  layout.widgets.length > MIN_VISIBLE_DASHBOARD_WIDGETS;

export const addDraftWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId
): DashboardLayout => {
  const definition = getDashboardWidget(widgetId);
  if (!definition) {
    return layout;
  }

  if (isWidgetVisibleInLayout(layout, widgetId)) {
    return layout;
  }

  return {
    columnCount: layout.columnCount,
    widgets: withSequentialOrder([
      ...layout.widgets,
      {
        id: widgetId,
        order: layout.widgets.length,
        widthSpan: definition.defaultWidthSpan,
      },
    ]),
  };
};

export const removeDraftWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId
): DashboardLayout => {
  if (!canRemoveDashboardWidget(layout)) {
    return layout;
  }

  if (!isWidgetVisibleInLayout(layout, widgetId)) {
    return layout;
  }

  return {
    columnCount: layout.columnCount,
    widgets: withSequentialOrder(layout.widgets.filter((widget) => widget.id !== widgetId)),
  };
};

export const swapDraftWidgets = (
  layout: DashboardLayout,
  sourceId: DashboardWidgetId,
  targetId: DashboardWidgetId
): DashboardLayout => {
  if (sourceId === targetId) {
    return layout;
  }

  const ordered = [...layout.widgets]
    .sort((left, right) => left.order - right.order)
    .map((widget) => ({
      id: widget.id,
      order: widget.order,
      widthSpan: widget.widthSpan,
    }));
  const sourceIndex = ordered.findIndex((widget) => widget.id === sourceId);
  const targetIndex = ordered.findIndex((widget) => widget.id === targetId);

  if (sourceIndex < 0 || targetIndex < 0) {
    return layout;
  }

  const source = ordered[sourceIndex];
  const target = ordered[targetIndex];
  // Keep each slot's width so packing (half/full rows) does not reflow.
  ordered[sourceIndex] = {
    id: target.id,
    order: sourceIndex,
    widthSpan: source.widthSpan,
  };
  ordered[targetIndex] = {
    id: source.id,
    order: targetIndex,
    widthSpan: target.widthSpan,
  };

  return {
    columnCount: layout.columnCount,
    widgets: ordered.map((widget, index) => ({
      id: widget.id,
      order: index,
      widthSpan: widget.widthSpan,
    })),
  };
};

export const moveDraftWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
  direction: DashboardWidgetMoveDirection,
  options: DashboardWidgetMoveOptions = {}
): DashboardLayout => {
  const neighborId = getNeighborWidgetId(layout, widgetId, direction, options);
  if (!neighborId) {
    return layout;
  }

  return swapDraftWidgets(layout, widgetId, neighborId);
};

export const canMoveDashboardWidget = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
  direction: DashboardWidgetMoveDirection,
  options: DashboardWidgetMoveOptions = {}
): boolean => getNeighborWidgetId(layout, widgetId, direction, options) !== null;

const usesGridMove = (
  layout: DashboardLayout,
  options: DashboardWidgetMoveOptions
): boolean => layout.columnCount === 2 && !options.forceSingleColumn;

interface MutableRow {
  widgets: DashboardWidgetPlacement[];
}

const toMutableRows = (layout: DashboardLayout): MutableRow[] =>
  buildDashboardLayoutRows(layout, { forceSingleColumn: false }).map((row) => ({
    widgets: row.widgets.map((widget) => ({
      id: widget.id,
      order: widget.order,
      widthSpan: widget.widthSpan,
    })),
  }));

const findWidgetLocation = (
  rows: MutableRow[],
  widgetId: DashboardWidgetId
): { rowIndex: number; slotIndex: number } | null => {
  for (let rowIndex = 0; rowIndex < rows.length; rowIndex += 1) {
    const slotIndex = rows[rowIndex].widgets.findIndex((widget) => widget.id === widgetId);
    if (slotIndex >= 0) {
      return { rowIndex, slotIndex };
    }
  }

  return null;
};

const isFullRow = (row: MutableRow): boolean =>
  row.widgets.length === 1 && row.widgets[0].widthSpan >= 2;

const getNeighborWidgetId = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
  direction: DashboardWidgetMoveDirection,
  options: DashboardWidgetMoveOptions
): DashboardWidgetId | null => {
  if (!usesGridMove(layout, options)) {
    if (direction === 'left' || direction === 'right') {
      return null;
    }

    const ordered = [...layout.widgets].sort((left, right) => left.order - right.order);
    const currentIndex = ordered.findIndex((widget) => widget.id === widgetId);
    if (currentIndex < 0) {
      return null;
    }

    const nextIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    return ordered[nextIndex]?.id ?? null;
  }

  const rows = toMutableRows(layout);
  const location = findWidgetLocation(rows, widgetId);
  if (!location) {
    return null;
  }

  const sourceRow = rows[location.rowIndex];

  if (direction === 'left') {
    return location.slotIndex > 0 ? sourceRow.widgets[location.slotIndex - 1].id : null;
  }

  if (direction === 'right') {
    return location.slotIndex < sourceRow.widgets.length - 1
      ? sourceRow.widgets[location.slotIndex + 1].id
      : null;
  }

  const targetRowIndex = location.rowIndex + (direction === 'up' ? -1 : 1);
  if (targetRowIndex < 0 || targetRowIndex >= rows.length) {
    return null;
  }

  const targetRow = rows[targetRowIndex];
  if (isFullRow(sourceRow) || isFullRow(targetRow)) {
    return targetRow.widgets[0].id;
  }

  const targetSlot = Math.min(location.slotIndex, targetRow.widgets.length - 1);
  return targetRow.widgets[targetSlot].id;
};

export const setDraftWidgetWidthSpan = (
  layout: DashboardLayout,
  widgetId: DashboardWidgetId,
  widthSpan: DashboardWidthSpan
): DashboardLayout => {
  if (widthSpan !== 1 && widthSpan !== 2) {
    return layout;
  }

  return {
    columnCount: layout.columnCount,
    widgets: layout.widgets.map((widget) =>
      widget.id === widgetId ? { ...widget, widthSpan } : widget
    ),
  };
};

export const setDraftColumnCount = (
  layout: DashboardLayout,
  columnCount: DashboardColumnCount
): DashboardLayout => {
  if (columnCount !== 1 && columnCount !== MAX_DASHBOARD_COLUMN_COUNT) {
    return layout;
  }

  if (layout.columnCount === columnCount) {
    return layout;
  }

  return {
    columnCount,
    widgets: layout.widgets.map((widget) => ({
      id: widget.id,
      order: widget.order,
      widthSpan: widget.widthSpan,
    })),
  };
};
